import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import {
  FiSearch,
  FiRefreshCw,
  FiUsers,
  FiTrendingUp,
  FiCalendar,
  FiUser,
  FiChevronRight,
  FiCheckCircle
} from 'react-icons/fi';
import styles from './AdminInvestmentsPage.module.css';
import {
  AdminInvestmentsService,
  AdminInvestmentProjectSummary,
  AdminProjectInvestmentsDetail
} from '../services/adminInvestments.service';
import { AdminDevelopmentsService } from '../services/adminDevelopments.service';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';

export default function AdminInvestmentsPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { addNotification } = useNotification();
  const isSpanish = i18n.language.startsWith('es');
  const locale = isSpanish ? 'es-MX' : 'en-US';

  if (user?.role === 'manager') {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const [search, setSearch] = useState('');
  const [onlyWithInvestments, setOnlyWithInvestments] = useState(true);
  const [minInvestments, setMinInvestments] = useState('');
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [projects, setProjects] = useState<AdminInvestmentProjectSummary[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [detail, setDetail] = useState<AdminProjectInvestmentsDetail | null>(null);

  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);
  const canFinalize = user?.role === 'admin';

  const [salePrice, setSalePrice] = useState('');
  const [generalExpenses, setGeneralExpenses] = useState('');
  const [reservePrice, setReservePrice] = useState('');

  const formatMoney = (amount: number, currency?: string | null) => {
    if (!currency || currency === 'MIXED') {
      return amount.toLocaleString(locale, { maximumFractionDigits: 0 });
    }
    return amount.toLocaleString(locale, { style: 'currency', currency, minimumFractionDigits: 0 });
  };

  const formatNumber = (value: number, digits = 4) => {
    if (!Number.isFinite(value)) return '—';
    return value.toLocaleString(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits });
  };

  const parseInputNumber = (value: string) => {
    const cleaned = value.replace(/,/g, '').trim();
    if (!cleaned) return 0;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
  };

  const computeMonthsBetween = (start: Date, end: Date) => {
    return (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  };

  const computeDaysBetween = (start: Date, end: Date) => {
    const diff = end.getTime() - start.getTime();
    if (!Number.isFinite(diff)) return 1;
    return Math.max(1, Math.ceil(diff / 86400000));
  };

  const formatDate = (value?: string | null) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatDateTime = (value?: string | null) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString(locale, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const loadProjects = async () => {
    setLoadingProjects(true);
    setError(null);
    try {
      const res = await AdminInvestmentsService.getProjects();
      setProjects(Array.isArray(res) ? res : []);
      setSelectedProjectId((prev) => {
        if (prev && res?.some((p: AdminInvestmentProjectSummary) => p.id === prev)) return prev;
        return res?.[0]?.id ?? null;
      });
    } catch {
      setProjects([]);
      setSelectedProjectId(null);
      setError(t('admin.investments_load_error') || 'No se pudieron cargar los proyectos con inversiones.');
    } finally {
      setLoadingProjects(false);
    }
  };

  const loadDetail = async (projectId: number) => {
    setLoadingDetail(true);
    setError(null);
    try {
      const res = await AdminInvestmentsService.getProjectDetail(projectId);
      setDetail(res || null);
    } catch {
      setDetail(null);
      setError(t('admin.investment_detail_load_error') || 'No se pudo cargar el detalle del proyecto.');
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    if (!selectedProjectId) {
      setDetail(null);
      return;
    }
    loadDetail(selectedProjectId);
  }, [selectedProjectId]);

  const openFinalize = () => {
    setSalePrice('');
    setGeneralExpenses('');
    setReservePrice('');
    setCloseError(null);
    setFinalizeOpen(true);
  };

  const closeFinalize = () => {
    if (closing) return;
    setFinalizeOpen(false);
  };

  const onExecuteClosure = async () => {
    if (!selectedProjectId) return;
    setClosing(true);
    setCloseError(null);

    try {
      const payload = {
        salePrice: parseInputNumber(salePrice),
        reservePrice: parseInputNumber(reservePrice),
        generalExpenses: parseInputNumber(generalExpenses),
      };

      await AdminInvestmentsService.closeProjectInvestments(selectedProjectId, payload);
      const finalizeResult = await AdminDevelopmentsService.finalize(selectedProjectId);

      if (Number(finalizeResult?.failedOrders || 0) > 0) {
        throw new Error(
          `El cierre terminó con ${finalizeResult.failedOrders} orden(es) fallida(s). Revisa la bitácora de auditoría.`
        );
      }

      addNotification(t('admin.investments_finalize_success') || 'Cierre ejecutado correctamente.', 'success');
      await loadProjects();
      await loadDetail(selectedProjectId);
      setFinalizeOpen(false);
    } catch (error: any) {
      const message =
        error?.message || t('admin.investments_finalize_error') || 'No se pudo ejecutar el cierre.';
      setCloseError(message);
      addNotification(message, 'error');
    } finally {
      setClosing(false);
    }
  };

  const isProjectClosedForList = (project?: { status?: string | null; projectState?: string | null; isActive?: boolean | null; closuresCount?: number | null }) => {
    if (!project) return false;
    const closures = Number(project.closuresCount || 0);
    return (
      closures > 0 ||
      project.status === 'COMPLETED' ||
      project.projectState === 'FINISHED' ||
      project.isActive === false
    );
  };

  const isProjectClosedForActions = (project?: { closuresCount?: number | null }) => {
    if (!project) return false;
    return Number(project.closuresCount || 0) > 0;
  };

  const [viewMode, setViewMode] = useState<'open' | 'closed'>('open');

  const filteredProjects = useMemo(() => {
    const minValue = Number(minInvestments);
    const term = search.trim().toLowerCase();
    let base = projects.filter((project) =>
      viewMode === 'closed' ? isProjectClosedForList(project) : !isProjectClosedForList(project)
    );

    if (onlyWithInvestments) {
      base = base.filter((p) => Number(p.investmentsCount || 0) > 0);
    }

    if (Number.isFinite(minValue) && minValue > 0) {
      base = base.filter((p) => Number(p.investmentsCount || 0) >= minValue);
    }

    if (!term) {
      return [...base].sort((a, b) => Number(b.totalInvested || 0) - Number(a.totalInvested || 0));
    }

    return base
      .filter((p) => {
      const devName = p.developer?.name?.toLowerCase() || '';
      const devEmail = p.developer?.email?.toLowerCase() || '';
      return (
        p.name.toLowerCase().includes(term) ||
        p.slug.toLowerCase().includes(term) ||
        devName.includes(term) ||
        devEmail.includes(term)
      );
    })
      .sort((a, b) => Number(b.totalInvested || 0) - Number(a.totalInvested || 0));
  }, [projects, search, onlyWithInvestments, minInvestments, viewMode]);

  const selectedProject = detail?.project ?? projects.find((p) => p.id === selectedProjectId) ?? null;
  const selectedClosed = isProjectClosedForActions(selectedProject ?? undefined);
  const needsRetry =
    !selectedClosed &&
    !!selectedProject &&
    (selectedProject.status === 'COMPLETED' ||
      selectedProject.projectState === 'FINISHED' ||
      selectedProject.isActive === false);

  const closurePreview = useMemo(() => {
    const investments = detail?.investments ?? [];
    const totalInvested = investments.reduce((sum, inv) => sum + Number(inv.amount || 0), 0);
    const earliestInvestment = investments.reduce<Date | null>((acc, inv) => {
      const d = new Date(inv.investedAt);
      if (Number.isNaN(d.getTime())) return acc;
      if (!acc || d < acc) return d;
      return acc;
    }, null);

    const projectStart = selectedProject?.investmentStartAt
      ? new Date(selectedProject.investmentStartAt)
      : earliestInvestment;
    const closeDate = selectedProject?.investmentEndAt
      ? new Date(selectedProject.investmentEndAt)
      : new Date();

    const closeDateSafe = Number.isNaN(closeDate.getTime()) ? new Date() : closeDate;
    const earliestSafe = earliestInvestment && !Number.isNaN(earliestInvestment.getTime()) ? earliestInvestment : closeDateSafe;

    const daysFirstInvestor = computeDaysBetween(earliestSafe, closeDateSafe);
    const termMonths = selectedProject?.termMonths ?? null;

    const baseRows = investments.map((inv) => {
      const amount = Number(inv.amount || 0);
      const investedAtDate = new Date(inv.investedAt);
      const investedAtSafe = Number.isNaN(investedAtDate.getTime()) ? closeDateSafe : investedAtDate;
      const daysInvestment = computeDaysBetween(investedAtSafe, closeDateSafe);
      const factorPlazo = daysFirstInvestor > 0 ? daysInvestment / daysFirstInvestor : 0;

      let monthsInvestment = 0;
      if (termMonths && termMonths > 0 && projectStart && !Number.isNaN(projectStart.getTime())) {
        const monthsSinceStart = computeMonthsBetween(projectStart, investedAtSafe) + 1;
        monthsInvestment = Math.max(1, termMonths - monthsSinceStart + 1);
      } else {
        monthsInvestment = Math.max(1, computeMonthsBetween(investedAtSafe, closeDateSafe) + 1);
      }

      const commission = amount * monthsInvestment * 0.01;

      return {
        ...inv,
        amount,
        investedAtSafe,
        monthsInvestment,
        daysInvestment,
        factorPlazo,
        commission,
      };
    });

    const sumFactorPlazo = baseRows.reduce((sum, r) => sum + r.factorPlazo, 0);
    const totalCommission = baseRows.reduce((sum, r) => sum + r.commission, 0);

    const sale = parseInputNumber(salePrice);
    const reserve = parseInputNumber(reservePrice);
    const expenses = parseInputNumber(generalExpenses);

    const utilidadNeta = sale - reserve - expenses - totalCommission;
    const profunding20 = utilidadNeta * 0.2;
    const cliente80 = utilidadNeta * 0.8;

    const rowsWithWeights = baseRows.map((row) => {
      const factorPlazoPonderado = sumFactorPlazo > 0 ? row.factorPlazo / sumFactorPlazo : 0;
      const factorInversionPonderado = totalInvested > 0 ? row.amount / totalInvested : 0;
      const factorClienteAjustado = factorPlazoPonderado * factorInversionPonderado;
      return {
        ...row,
        factorPlazoPonderado,
        factorInversionPonderado,
        factorClienteAjustado,
      };
    });

    const sumFactorClienteAjustado = rowsWithWeights.reduce((sum, r) => sum + r.factorClienteAjustado, 0);

    const rowsFinal = rowsWithWeights.map((row) => {
      const utilidad =
        sumFactorClienteAjustado > 0
          ? (row.factorClienteAjustado / sumFactorClienteAjustado) * cliente80
          : 0;
      const tir =
        row.amount > 0 && row.monthsInvestment > 0
          ? (utilidad / row.amount / row.monthsInvestment) * 12
          : 0;
      const totalDevolver = row.amount + utilidad;
      return {
        ...row,
        utilidad,
        tir,
        totalDevolver,
      };
    });

    return {
      totalInvested,
      totalCommission,
      sumFactorPlazo,
      sumFactorClienteAjustado,
      utilidadNeta,
      profunding20,
      cliente80,
      closeDate: closeDateSafe,
      termMonths,
      rows: rowsFinal,
    };
  }, [
    detail?.investments,
    selectedProject?.investmentStartAt,
    selectedProject?.investmentEndAt,
    selectedProject?.termMonths,
    salePrice,
    reservePrice,
    generalExpenses,
  ]);

  const headerStats = useMemo(() => {
    const totalProjects = projects.length;
    const totalInvested = projects.reduce((sum, p) => sum + Number(p.totalInvested || 0), 0);
    const totalInvestors = projects.reduce((sum, p) => sum + Number(p.investorsCount || 0), 0);
    return { totalProjects, totalInvested, totalInvestors };
  }, [projects]);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerText}>
          <h1 className={styles.title}>{t('admin.investments_title') || 'Inversiones'}</h1>
          <p className={styles.subtitle}>
            {t('admin.investments_subtitle') || 'Consulta inversiones por proyecto y revisa a los inversionistas.'}
          </p>
        </div>

        <button type="button" className={styles.refreshBtn} onClick={loadProjects} disabled={loadingProjects}>
          <FiRefreshCw /> {t('common.refresh') || 'Actualizar'}
        </button>
      </header>

      <section className={styles.statsRow} aria-label="stats">
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <FiTrendingUp />
          </div>
          <div className={styles.statBody}>
            <div className={styles.statLabel}>{t('admin.investments_total') || 'Total proyectos'}</div>
            <div className={styles.statValue}>{loadingProjects ? '—' : String(headerStats.totalProjects)}</div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <FiTrendingUp />
          </div>
          <div className={styles.statBody}>
            <div className={styles.statLabel}>{t('admin.total_invested') || 'Total invertido'}</div>
            <div className={styles.statValue}>{loadingProjects ? '—' : formatMoney(headerStats.totalInvested, 'MXN')}</div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <FiUsers />
          </div>
          <div className={styles.statBody}>
            <div className={styles.statLabel}>{t('admin.table_user') || 'Inversionistas'}</div>
            <div className={styles.statValue}>{loadingProjects ? '—' : String(headerStats.totalInvestors)}</div>
          </div>
        </div>
      </section>

      {error ? <div className={styles.error}>{error}</div> : null}

      <div className={styles.contentGrid}>
        <section className={styles.listPanel}>
          <div className={styles.listHeader}>
            <div className={styles.listTabs}>
              <button
                type="button"
                className={viewMode === 'open' ? styles.tabActive : styles.tabButton}
                onClick={() => setViewMode('open')}
              >
                {t('admin.investments_tab_open') || 'Inversiones abiertas'}
              </button>
              <button
                type="button"
                className={viewMode === 'closed' ? styles.tabActive : styles.tabButton}
                onClick={() => setViewMode('closed')}
              >
                {t('admin.investments_tab_closed') || 'Inversiones cerradas'}
              </button>
            </div>

            <div className={styles.searchField} role="search">
              <FiSearch className={styles.searchIcon} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={styles.searchInput}
                placeholder={t('admin.search_investments_placeholder') || 'Buscar proyectos o desarrolladores...'}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setSearch('');
                }}
                disabled={loadingProjects}
              />
            </div>

            <div className={styles.filtersRow}>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={onlyWithInvestments}
                  onChange={(e) => setOnlyWithInvestments(e.target.checked)}
                  disabled={loadingProjects}
                />
                <span>{t('admin.investments_filter_with_investments') || 'Solo con inversiones'}</span>
              </label>

              <label className={styles.minFilter}>
                <span>{t('admin.investments_min_investments') || 'Mínimo de inversiones'}</span>
                <input
                  type="number"
                  min="0"
                  value={minInvestments}
                  onChange={(e) => setMinInvestments(e.target.value)}
                  placeholder={t('admin.investments_min_placeholder') || 'Ej. 10'}
                  disabled={loadingProjects}
                />
              </label>
            </div>
          </div>

          <div className={styles.projectList}>
            {loadingProjects ? (
              Array.from({ length: 6 }).map((_, idx) => (
                <div key={`sk-${idx}`} className={styles.projectSkeleton} />
              ))
            ) : filteredProjects.length === 0 ? (
              <div className={styles.empty}>
                <div className={styles.emptyTitle}>{t('admin.no_investments_found') || 'No se encontraron proyectos.'}</div>
                <div className={styles.emptyText}>
                  {t('admin.no_investments_hint') || 'Intenta con otro término o revisa que existan inversiones.'}
                </div>
              </div>
            ) : (
              filteredProjects.map((project) => {
                const isSelected = project.id === selectedProjectId;
                const closed = isProjectClosedForList(project);
                return (
                  <button
                    key={project.id}
                    type="button"
                    className={`${styles.projectCard} ${isSelected ? styles.projectCardActive : ''}`}
                    onClick={() => setSelectedProjectId(project.id)}
                  >
                    <div className={styles.projectMain}>
                      <div>
                        <div className={styles.projectTitleRow}>
                          <span className={styles.projectTitle}>{project.name}</span>
                          {closed ? (
                            <span className={styles.closedBadge}>
                              {t('admin.investments_closed_badge') || 'Cerrado'}
                            </span>
                          ) : null}
                        </div>
                        <div className={styles.projectSub}>{project.developer?.name || 'Desarrollador sin asignar'}</div>
                      </div>
                      <FiChevronRight className={styles.chevron} />
                    </div>

                    <div className={styles.projectMeta}>
                      <span>
                        <FiCalendar /> {formatDate(project.investmentStartAt)}
                      </span>
                      <span>
                        <FiCalendar /> {formatDate(project.investmentEndAt)}
                      </span>
                    </div>

                    <div className={styles.projectStats}>
                      <div>
                        <div className={styles.projectStatLabel}>{t('admin.investments_total') || 'Total invertido'}</div>
                        <div className={styles.projectStatValue}>
                          {formatMoney(project.totalInvested, project.currency || 'MXN')}
                        </div>
                      </div>
                      <div>
                        <div className={styles.projectStatLabel}>{t('admin.investments_active') || 'Inversiones'}</div>
                        <div className={styles.projectStatValue}>{project.investmentsCount}</div>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </section>

        <section className={styles.detailPanel}>
          {!selectedProjectId ? (
            <div className={styles.detailEmpty}>
              <div className={styles.emptyTitle}>{t('admin.no_investments_found') || 'Selecciona un proyecto.'}</div>
              <div className={styles.emptyText}>Elige un proyecto para ver su detalle e inversiones.</div>
            </div>
          ) : loadingDetail ? (
            <div className={styles.detailLoading}>{t('common.loading') || 'Cargando...'}</div>
          ) : !detail ? (
            <div className={styles.detailEmpty}>
              <div className={styles.emptyTitle}>{t('admin.investment_detail_load_error') || 'No se pudo cargar el detalle.'}</div>
            </div>
          ) : (
            <>
              <div className={styles.detailHeader}>
                <div>
                  <div className={styles.detailTitleRow}>
                    <h2 className={styles.detailTitle}>{selectedProject?.name}</h2>
                    {selectedClosed ? (
                      <span className={styles.closedBadge}>
                        {t('admin.investments_closed_badge') || 'Cerrado'}
                      </span>
                    ) : null}
                  </div>
                  <div className={styles.detailSub}>
                    <FiUser /> {selectedProject?.developer?.name || 'Desarrollador sin asignar'}
                    {selectedProject?.developer?.email ? ` • ${selectedProject.developer.email}` : ''}
                  </div>
                </div>

                <div className={styles.detailHeaderSide}>
                  <div className={styles.detailDates}>
                    <div>
                      <span>{t('common.from') || 'Desde'}</span>
                      <strong>{formatDate(selectedProject?.investmentStartAt)}</strong>
                    </div>
                    <div>
                      <span>{t('common.to') || 'Hasta'}</span>
                      <strong>{formatDate(selectedProject?.investmentEndAt)}</strong>
                    </div>
                  </div>

                  {canFinalize && !selectedClosed ? (
                    <button type="button" className={styles.finalizeButton} onClick={openFinalize}>
                      <FiCheckCircle /> {needsRetry
                        ? t('admin.investments_retry_close') || 'Reintentar cierre'
                        : t('admin.investments_finalize_action') || 'Cerrar inversiones'}
                    </button>
                  ) : selectedClosed ? (
                    <div className={styles.closedHint}>
                      {t('admin.investments_closed_hint') || 'Inversiones cerradas.'}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className={styles.detailStats}>
                <div>
                  <div className={styles.detailStatLabel}>{t('admin.investments_total') || 'Total invertido'}</div>
                  <div className={styles.detailStatValue}>
                    {formatMoney(detail.project.totalInvested, detail.project.currency || 'MXN')}
                  </div>
                </div>
                <div>
                  <div className={styles.detailStatLabel}>{t('admin.investments_active') || 'Inversiones'}</div>
                  <div className={styles.detailStatValue}>{detail.project.investmentsCount}</div>
                </div>
                <div>
                  <div className={styles.detailStatLabel}>{t('admin.table_user') || 'Inversionistas'}</div>
                  <div className={styles.detailStatValue}>{detail.project.investorsCount}</div>
                </div>
              </div>

              {detail.closureSummary?.ordersClosed ? (
                <div className={styles.closureSummary}>
                  <div>
                    <div className={styles.detailStatLabel}>
                      {t('admin.investments_close_total_capital') || 'Capital devuelto'}
                    </div>
                    <div className={styles.detailStatValue}>
                      {formatMoney(detail.closureSummary.totalCapital, detail.project.currency || 'MXN')}
                    </div>
                  </div>
                  <div>
                    <div className={styles.detailStatLabel}>
                      {t('admin.investments_close_total_yield') || 'Utilidad total'}
                    </div>
                    <div className={styles.detailStatValue}>
                      {formatMoney(detail.closureSummary.totalYield, detail.project.currency || 'MXN')}
                    </div>
                  </div>
                  <div>
                    <div className={styles.detailStatLabel}>
                      {t('admin.investments_close_total_return') || 'Total devuelto'}
                    </div>
                    <div className={styles.detailStatValue}>
                      {formatMoney(detail.closureSummary.totalReturned, detail.project.currency || 'MXN')}
                    </div>
                  </div>
                </div>
              ) : needsRetry ? (
                <div className={styles.retryHint}>
                  {t('admin.investments_retry_hint') ||
                    'El proyecto está finalizado pero no se encontraron cierres. Reintenta el cierre.'}
                </div>
              ) : null}

              <div className={styles.detailTableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>{t('admin.table_user') || 'Inversionista'}</th>
                      <th>{t('admin.table_amount') || 'Monto'}</th>
                      <th>{t('admin.table_date') || 'Fecha'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.investments.length === 0 ? (
                      <tr>
                        <td colSpan={3}>
                          <div className={styles.emptyInline}>
                            {t('admin.no_investments_found') || 'No hay inversiones para este proyecto.'}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      detail.investments.map((inv) => (
                        <tr key={`${inv.orderId}-${inv.userId}`}>
                          <td>
                            <div className={styles.cellMain}>{inv.userName}</div>
                            <div className={styles.cellSub}>{inv.userEmail}</div>
                          </td>
                          <td>
                            <div className={styles.cellMain}>{formatMoney(inv.amount, inv.currency)}</div>
                            <div className={styles.cellSub}>Orden #{inv.orderId}</div>
                          </td>
                          <td>
                            <div className={styles.cellMain}>{formatDateTime(inv.investedAt)}</div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      </div>

      {finalizeOpen && selectedProject ? (
        <div className={styles.modalOverlay} onClick={closeFinalize}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <button type="button" className={styles.closeButton} onClick={closeFinalize}>
              ×
            </button>
            <h2 className={styles.modalTitle}>{t('admin.investments_finalize_title') || 'Cierre de inversiones'}</h2>
            <div className={styles.modalBody}>
              <p className={styles.modalHint}>
                {t('admin.investments_finalize_hint') ||
                  'Al cerrar el proyecto se acreditarán los fondos disponibles a los inversionistas.'}
              </p>

              <div className={styles.formSection}>
                <div className={styles.formGrid}>
                  <label className={styles.field}>
                    <span>{t('admin.investments_sale_price') || 'Precio de venta (final)'}</span>
                    <input
                      type="number"
                      min="0"
                      value={salePrice}
                      onChange={(e) => setSalePrice(e.target.value)}
                      placeholder={t('admin.investments_sale_price_placeholder') || 'Ej. 1,250,000'}
                    />
                  </label>

                  <label className={styles.field}>
                    <span>{t('admin.investments_general_expenses') || 'Gastos generales'}</span>
                    <input
                      type="number"
                      min="0"
                      value={generalExpenses}
                      onChange={(e) => setGeneralExpenses(e.target.value)}
                      placeholder={t('admin.investments_general_expenses_placeholder') || 'Ej. 80,000'}
                    />
                  </label>

                  <label className={styles.field}>
                    <span>{t('admin.investments_reserve_price') || 'Precio reserva (compra)'}</span>
                    <input
                      type="number"
                      min="0"
                      value={reservePrice}
                      onChange={(e) => setReservePrice(e.target.value)}
                      placeholder={t('admin.investments_reserve_price_placeholder') || 'Ej. 950,000'}
                    />
                  </label>
                </div>

                <div className={styles.summaryGrid}>
                  <div className={styles.summaryItem}>
                    <div className={styles.summaryLabel}>{t('admin.investments_commission_total') || 'Comisión total Profunding'}</div>
                    <div className={styles.summaryValue}>{formatMoney(closurePreview.totalCommission, selectedProject?.currency || 'MXN')}</div>
                    <div className={styles.summaryHint}>{t('admin.investments_commission_hint') || '1% mensual por inversión'}</div>
                  </div>
                  <div className={styles.summaryItem}>
                    <div className={styles.summaryLabel}>{t('admin.investments_net_profit') || 'Utilidad neta'}</div>
                    <div className={styles.summaryValue}>{formatMoney(closurePreview.utilidadNeta, selectedProject?.currency || 'MXN')}</div>
                  </div>
                  <div className={styles.summaryItem}>
                    <div className={styles.summaryLabel}>{t('admin.investments_profunding_share') || '20% Profunding'}</div>
                    <div className={styles.summaryValue}>{formatMoney(closurePreview.profunding20, selectedProject?.currency || 'MXN')}</div>
                  </div>
                  <div className={styles.summaryItem}>
                    <div className={styles.summaryLabel}>{t('admin.investments_client_share') || '80% Cliente'}</div>
                    <div className={styles.summaryValue}>{formatMoney(closurePreview.cliente80, selectedProject?.currency || 'MXN')}</div>
                  </div>
                </div>
              </div>

              {closeError ? <div className={styles.modalError}>{closeError}</div> : null}

              <div className={styles.tableSection}>
                <div className={styles.tableHeader}>
                  <div>
                    <div className={styles.tableTitle}>{t('admin.investments_detail_title') || 'Detalle por inversión'}</div>
                    <div className={styles.tableSubtitle}>
                      {t('admin.investments_detail_subtitle') || 'Distribución de utilidad según plazo e inversión.'}
                    </div>
                  </div>
                  <div className={styles.tableMeta}>
                    <div>
                      <span>{t('admin.investments_close_date') || 'Fecha cierre'}</span>
                      <strong>{formatDateTime(closurePreview.closeDate.toISOString())}</strong>
                    </div>
                    <div>
                      <span>{t('admin.investments_term_months') || 'Plazo (meses)'}</span>
                      <strong>{closurePreview.termMonths ?? '—'}</strong>
                    </div>
                  </div>
                </div>

                <div className={styles.tableWrapLarge}>
                  <table className={styles.detailTable}>
                    <thead>
                      <tr>
                        <th>{t('admin.table_user') || 'Inversionista'}</th>
                        <th>{t('admin.table_amount') || 'Monto'}</th>
                        <th>{t('admin.investments_months') || 'Plazo (meses)'}</th>
                        <th>{t('admin.investments_date') || 'Fecha inversión'}</th>
                        <th>{t('admin.investments_commission') || 'Comisión'}</th>
                        <th>{t('admin.investments_factor_plazo') || 'Factor plazo'}</th>
                        <th>{t('admin.investments_factor_plazo_weighted') || 'Factor plazo pond.'}</th>
                        <th>{t('admin.investments_factor_investment_weighted') || 'Factor inversión pond.'}</th>
                        <th>{t('admin.investments_factor_cliente_adjusted') || 'Factor cliente ajust.'}</th>
                        <th>{t('admin.investments_utility') || 'Utilidad'}</th>
                        <th>{t('admin.investments_tir') || 'TIR'}</th>
                        <th>{t('admin.investments_total_return') || 'Total a devolver'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {closurePreview.rows.length === 0 ? (
                        <tr>
                          <td colSpan={12} className={styles.emptyCell}>
                            {t('admin.no_investments_found') || 'No hay inversiones para este proyecto.'}
                          </td>
                        </tr>
                      ) : (
                        closurePreview.rows.map((row) => (
                          <tr key={`${row.orderId}-${row.userId}`}>
                            <td>
                              <div className={styles.cellMain}>{row.userName}</div>
                              <div className={styles.cellSub}>{row.userEmail}</div>
                            </td>
                            <td>
                              <div className={styles.cellMain}>{formatMoney(row.amount, row.currency)}</div>
                              <div className={styles.cellSub}>Orden #{row.orderId}</div>
                            </td>
                            <td>{row.monthsInvestment}</td>
                            <td>{formatDateTime(row.investedAt)}</td>
                            <td>{formatMoney(row.commission, row.currency)}</td>
                            <td>{formatNumber(row.factorPlazo, 4)}</td>
                            <td>{formatNumber(row.factorPlazoPonderado, 4)}</td>
                            <td>{formatNumber(row.factorInversionPonderado, 4)}</td>
                            <td>{formatNumber(row.factorClienteAjustado, 4)}</td>
                            <td>{formatMoney(row.utilidad, row.currency)}</td>
                            <td>{formatNumber(row.tir * 100, 2)}%</td>
                            <td>{formatMoney(row.totalDevolver, row.currency)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    {closurePreview.rows.length > 0 ? (
                      <tfoot>
                        <tr>
                          <td className={styles.footerLabel} colSpan={2}>
                            {t('admin.investments_totals') || 'Totales'}
                          </td>
                          <td>{closurePreview.rows.length}</td>
                          <td />
                          <td>{formatMoney(closurePreview.totalCommission, selectedProject?.currency || 'MXN')}</td>
                          <td>{formatNumber(closurePreview.sumFactorPlazo, 4)}</td>
                          <td />
                          <td />
                          <td>{formatNumber(closurePreview.sumFactorClienteAjustado, 4)}</td>
                          <td>{formatMoney(closurePreview.cliente80, selectedProject?.currency || 'MXN')}</td>
                          <td />
                          <td>{formatMoney(closurePreview.totalInvested + closurePreview.cliente80, selectedProject?.currency || 'MXN')}</td>
                        </tr>
                      </tfoot>
                    ) : null}
                  </table>
                </div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <div className={styles.footerActions}>
                <button type="button" className={styles.ghostButton} onClick={closeFinalize} disabled={closing}>
                  {t('common.back') || 'Volver'}
                </button>
                <button type="button" className={styles.primaryButton} onClick={onExecuteClosure} disabled={closing}>
                  {closing
                    ? t('common.processing') || 'Procesando...'
                    : t('admin.investments_finalize_confirm') || 'Ejecutar cierre'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
